/** Map ISO 4217 currency codes to BCP 47 locales for Intl formatting. */
const CURRENCY_LOCALES: Record<string, string> = {
  INR: 'en-IN',
  USD: 'en-US',
  GBP: 'en-GB',
  EUR: 'de-DE',
  AED: 'en-AE',
  SGD: 'en-SG',
  AUD: 'en-AU',
  CAD: 'en-CA',
  JPY: 'ja-JP',
  ZAR: 'en-ZA',
};

/** Same glyph map as mobile MoneyFormat / backend currencyEngine. */
const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
  ZAR: 'R',
  AED: 'د.إ',
  SGD: 'S$',
  AUD: 'A$',
  CAD: 'C$',
  JPY: '¥',
  XOF: 'F CFA',
  XAF: 'F CFA',
  CFA: 'F CFA',
};

function cfaDisplayLabel(raw: string): string | null {
  const compact = raw.toUpperCase().replace(/[\s.]/g, '');
  if (compact === 'XOF' || compact === 'XAF' || compact === 'FCFA' || compact === 'CFA') {
    return 'F CFA';
  }
  return null;
}

/** Tenant/session default — set from live tenant, never a hardcoded rupee/dollar. */
let activeCurrencyCode = '';
let activeSymbol = '';

export const MONEY_DECIMALS = 2;

/** How the tenant's currency is written, from the Currencies master (via /tenants/current). */
export interface CurrencyDisplayFormat {
  code: string;
  symbol: string;
  decimalPlaces: number;
  decimalSeparator: string;
  thousandSeparator: string;
  position: 'before' | 'after';
}

let activeFormat: CurrencyDisplayFormat | null = null;

export function getActiveCurrencyFormat(): CurrencyDisplayFormat | null {
  return activeFormat;
}

export function setActiveCurrencyFormat(format?: Partial<CurrencyDisplayFormat> | null): void {
  const code = String(format?.code ?? '').trim().toUpperCase();
  if (!format || !/^[A-Z]{3}$/.test(code)) {
    activeFormat = null;
  } else {
    const places = Number(format.decimalPlaces);
    activeFormat = {
      code,
      symbol: String(format.symbol ?? '').trim() || CURRENCY_SYMBOLS[code] || code,
      decimalPlaces: Number.isInteger(places) && places >= 0 && places <= 4 ? places : defaultFractionDigits(code),
      decimalSeparator: format.decimalSeparator || '.',
      thousandSeparator: format.thousandSeparator ?? ',',
      position: format.position === 'after' ? 'after' : 'before',
    };
  }
  if (activeCurrencyCode) activeSymbol = symbolFrom(activeCurrencyCode);
}

function formatFor(code: string): CurrencyDisplayFormat | null {
  return activeFormat && code && activeFormat.code === code ? activeFormat : null;
}

export function getActiveCurrencyCode(): string {
  return activeCurrencyCode;
}

export function getActiveSymbol(): string {
  return activeSymbol;
}

export function setActiveCurrency(codeOrSymbol?: string | null): void {
  const raw = String(codeOrSymbol ?? '').trim();
  if (!raw) {
    activeCurrencyCode = '';
    activeSymbol = '';
    return;
  }
  if (/^[A-Za-z]{3}$/.test(raw)) {
    activeCurrencyCode = raw.toUpperCase();
    activeSymbol = symbolFrom(activeCurrencyCode);
    return;
  }
  activeSymbol = raw;
}

/** ISO code or existing symbol → display glyph. Empty input uses tenant active symbol. */
export function symbolFrom(codeOrSymbol?: string | null): string {
  const raw = String(codeOrSymbol ?? '').trim();
  if (!raw) return activeSymbol;
  const cfa = cfaDisplayLabel(raw);
  if (cfa) return cfa;
  if (/^[A-Za-z]{3}$/.test(raw)) {
    const up = raw.toUpperCase();
    return formatFor(up)?.symbol || CURRENCY_SYMBOLS[up] || up;
  }
  return raw;
}

export function normalizeCurrencyCode(currency?: string | null): string {
  const raw = String(currency ?? '').trim().toUpperCase();
  if (/^[A-Z]{3}$/.test(raw)) return raw;
  if (/^[A-Z]{3}$/.test(activeCurrencyCode)) return activeCurrencyCode;
  return '';
}

export function getCurrencyLocale(currency?: string | null): string {
  const code = normalizeCurrencyCode(currency);
  return (code && CURRENCY_LOCALES[code]) || 'en-US';
}

export interface FormatCurrencyOptions {
  /** Always 2 unless the currency has no minor units (JPY). */
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  /** Use compact notation for charts (e.g. ₹1.2L, $12K). */
  compact?: boolean;
}

function defaultFractionDigits(code: string): number {
  return code === 'JPY' ? 0 : MONEY_DECIMALS;
}

function pickStr(value: unknown): string | undefined {
  if (value == null) return undefined;
  const s = String(value).trim();
  return s || undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

/**
 * Per-record zone/tenant currency (ride/order/fare/wallet).
 * Never mix trips from different zones — pass the row as `from`.
 */
export function currencyFrom(source?: unknown): string {
  if (source == null) return activeSymbol;
  if (typeof source === 'string') return symbolFrom(source);

  const obj = asRecord(source);
  if (!obj) return activeSymbol;

  const fare = asRecord(obj.fare);
  const wallet = asRecord(obj.wallet);
  const zone = asRecord(obj.zone) || asRecord(obj.zoneId);
  const pricing = asRecord(obj.pricing);

  return symbolFrom(
    pickStr(obj.requestedCurrencySymbol)
    ?? pickStr(obj.requestedCurrencyCode)
    ?? pickStr(obj.currencySymbol)
    ?? pickStr(obj.currency_symbol)
    ?? pickStr(fare?.currencySymbol)
    ?? pickStr(fare?.currency_symbol)
    ?? pickStr(fare?.currency)
    ?? pickStr(obj.currency)
    ?? pickStr(obj.currencyCode)
    ?? pickStr(wallet?.currencySymbol)
    ?? pickStr(wallet?.currency)
    ?? pickStr(zone?.currency)
    ?? pickStr(pricing?.currency)
    ?? pickStr(obj.displayCurrency),
  );
}

export interface FormatMoneyOptions {
  currency?: string | null;
  symbol?: string | null;
  from?: unknown;
}

function isFormatMoneyOptions(value: unknown): value is FormatMoneyOptions {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const o = value as Record<string, unknown>;
  const keys = Object.keys(o);
  return keys.length > 0 && keys.every((k) => k === 'currency' || k === 'symbol' || k === 'from');
}

export type FormatMoneyArg = string | null | undefined | FormatMoneyOptions | object;

/** Resolve display symbol: explicit → record → tenant active. Never invent ₹. */
export function resolveFormatSymbol(currencyOrOpts?: FormatMoneyArg): string {
  if (currencyOrOpts == null || currencyOrOpts === '') {
    return activeSymbol;
  }
  if (typeof currencyOrOpts === 'string') {
    return symbolFrom(currencyOrOpts);
  }
  if (isFormatMoneyOptions(currencyOrOpts)) {
    if (currencyOrOpts.symbol) return symbolFrom(currencyOrOpts.symbol);
    if (currencyOrOpts.from != null) {
      const fromSym = currencyFrom(currencyOrOpts.from);
      if (fromSym) return fromSym;
    }
    if (currencyOrOpts.currency) return symbolFrom(currencyOrOpts.currency);
    return activeSymbol;
  }
  return currencyFrom(currencyOrOpts);
}

/** Currency symbol for field labels (e.g. ₹, $). Empty when tenant/record currency is unset. */
export function getCurrencySymbol(currency?: string | null | FormatMoneyArg): string {
  if (currency && typeof currency === 'object') return resolveFormatSymbol(currency);
  const trimmed = String(currency ?? '').trim();
  if (!trimmed) return activeSymbol;
  return symbolFrom(trimmed);
}

/** Label suffix for charge fields, e.g. "Base price (₹)". */
export function chargeFieldLabel(label: string, currency?: string | null | FormatMoneyArg): string {
  const symbol = getCurrencySymbol(currency);
  return symbol ? `${label} (${symbol})` : label;
}

/** Drop a hardcoded "(₹)" / "($)" / "(INR)" / "(Rs)" suffix from a backend-provided label. */
export function stripCurrencyLabelSuffix(label: string): string {
  return label.replace(/\s*\((₹|\$|INR|Rs\.?)\)\s*$/i, '');
}

function parseAmount(amount: unknown): number | null {
  if (amount == null || amount === '') return null;
  const n = typeof amount === 'number' ? amount : Number(amount);
  return Number.isFinite(n) ? n : null;
}

function codeFor(currencyOrOpts?: FormatMoneyArg): string {
  let code = '';
  if (typeof currencyOrOpts === 'string') code = normalizeCurrencyCode(currencyOrOpts);
  else if (isFormatMoneyOptions(currencyOrOpts)) code = normalizeCurrencyCode(currencyOrOpts.currency);
  else if (currencyOrOpts && typeof currencyOrOpts === 'object') {
    const rec = currencyOrOpts as Record<string, unknown>;
    const fare = asRecord(rec.fare);
    code = normalizeCurrencyCode(
      pickStr(rec.requestedCurrencyCode)
      || pickStr(rec.currency)
      || pickStr(fare?.currency),
    );
  }
  return code || activeCurrencyCode;
}

function groupDigits(value: number, digits: number, thousandSep: string, decimalSep: string): string {
  const [intPart, fracPart] = Math.abs(value).toFixed(digits).split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
  return fracPart ? `${grouped}${decimalSep}${fracPart}` : grouped;
}

/** Format amount using the same display label as mobile (never mix XOF vs F CFA). */
export function formatCurrency(
  amount: number,
  currency?: string | null,
  options: FormatCurrencyOptions = {},
): string {
  const {
    compact = false,
  } = options;
  const n = Number(amount);
  const value = Number.isFinite(n) ? n : 0;
  const symbol = symbolFrom(currency);
  if (compact) {
    const compactNum = new Intl.NumberFormat('en', {
      notation: 'compact',
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(value);
    if (!symbol) return compactNum;
    return formatFor(codeFor(currency))?.position === 'after' ? `${compactNum} ${symbol}` : `${symbol}${compactNum}`;
  }
  return formatMoney(value, currency);
}

/**
 * Display helper for tables/invoices — symbol + 2 decimals, same as mobile MoneyFormat.amount.
 * Pass the ride/order/zone row as the second argument so mixed-zone lists stay correct.
 */
export function formatMoney(amount: unknown, currencyOrOpts?: FormatMoneyArg): string {
  const symbol = resolveFormatSymbol(currencyOrOpts);
  const n = parseAmount(amount);
  if (n == null) return symbol ? `${symbol}--` : '--';
  const code = codeFor(currencyOrOpts);
  const fmt = formatFor(code);
  const digits = fmt ? fmt.decimalPlaces : defaultFractionDigits(code);
  const body = groupDigits(n, digits, fmt?.thousandSeparator ?? ',', fmt?.decimalSeparator ?? '.');
  const sign = n < 0 && Number(n.toFixed(digits)) !== 0 ? '-' : '';
  if (fmt?.position === 'after') return symbol ? `${sign}${body} ${symbol}` : `${sign}${body}`;
  return `${sign}${symbol}${body}`;
}

/** Signed wallet movement, e.g. +$12.00 / -$12.00 */
export function formatMoneySigned(
  amount: unknown,
  currencyOrOpts?: FormatMoneyArg,
  credit = true,
): string {
  const formatted = formatMoney(
    Math.abs(parseAmount(amount) ?? 0),
    currencyOrOpts,
  );
  if (parseAmount(amount) == null && resolveFormatSymbol(currencyOrOpts)) {
    const symbol = resolveFormatSymbol(currencyOrOpts);
    return credit ? `+${symbol}--` : `-${symbol}--`;
  }
  return credit ? `+${formatted}` : `-${formatted}`;
}

/** @deprecated Use formatMoney with tenant/record currency — does not force INR. */
export function formatInr(amount: number): string {
  return formatMoney(amount);
}

/** Compact currency label for chart axes and tooltips. */
export function formatCurrencyCompact(amount: number, currency?: string | null): string {
  return formatCurrency(amount, currency, { compact: true, minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Whole-number alias kept for callers; amounts still show 2 decimals (except JPY). */
export function formatCurrencyWhole(amount: number, currency?: string | null): string {
  return formatMoney(amount, currency);
}

export function isCurrencyField(field: { type?: string; key?: string }): boolean {
  return field.type === 'currency'
    || field.key === 'price'
    || field.key === 'costPrice'
    || field.key === 'fee';
}

/** Format currency input display — use with a separate currency-symbol adornment in form fields. */
export function formatCurrencyInputValue(value: unknown): string {
  if (value === '' || value == null) return '';
  if (typeof value === 'string' && value.endsWith('.')) return value;
  const parsed = typeof value === 'number'
    ? value
    : Number(String(value).trim().replace(/[^\d.-]/g, ''));
  if (!Number.isFinite(parsed)) return '';
  return parsed.toFixed(2);
}

/** Strip currency symbols/spaces for editable currency input parsing. */
export function sanitizeCurrencyInput(raw: string): string {
  return raw.replace(/[^\d.]/g, '');
}
